import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../api';
import { useAuth } from '../context/AuthContext';

export default function AiLogs() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ search:'', from:'', to:'', rated:'' });
  const [open, setOpen] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = {}; Object.entries(f).forEach(([k,v]) => { if (v) params[k]=v; });
    api.get('/ai/logs', { params }).then(r => setRows(r.data)).catch(()=>{}).finally(()=>setLoading(false));
  }, [f]);
  useEffect(() => { load(); }, [load]);

  // Export what the current filters show — not just the 100 rows on screen —
  // with the full answer text, which the table only shows one row at a time.
  const exportXlsx = async () => {
    setExporting(true);
    let rows = [];
    try {
      const params = { limit: 5000 };
      Object.entries(f).forEach(([k,v]) => { if (v) params[k]=v; });
      rows = (await api.get('/ai/logs', { params })).data;
    } catch (e) {
      setExporting(false);
      alert('Could not fetch the logs to export.');
      return;
    }
    setExporting(false);
    if (!rows.length) { alert('Nothing to export — the current filters return no questions.'); return; }

    const line = r => ({
      'When': new Date(r.created_at).toLocaleString('en-IN'),
      'Who': r.user_name || '—',
      'Question': r.question,
      'Answer': r.answer,
      'Answered from': r.used_docs ? 'School documents' : 'General knowledge',
      'Rating': r.helpful === true ? 'Helpful' : r.helpful === false ? 'Not helpful' : 'Unrated',
      'Model': r.model || '',
      'Seconds': r.ms ? +(r.ms/1000).toFixed(1) : '',
      'Sources': Array.isArray(r.sources) ? r.sources.map(s => s.title).join('; ') : '',
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows.map(line));
    ws['!cols'] = [{ wch:18 },{ wch:20 },{ wch:50 },{ wch:80 },{ wch:18 },{ wch:12 },{ wch:18 },{ wch:9 },{ wch:30 }];
    XLSX.utils.book_append_sheet(wb, ws, 'Questions');

    // Who is asking, and how often — the question an IT head actually has.
    const tally = (keyFn) => {
      const m = {};
      rows.forEach(r => {
        const k = keyFn(r) || '—';
        (m[k] = m[k] || { Questions:0, 'Not helpful':0 });
        m[k].Questions++;
        if (r.helpful === false) m[k]['Not helpful']++;
      });
      return Object.entries(m).sort((a,b) => b[1].Questions - a[1].Questions)
        .map(([k,v]) => ({ Name:k, ...v }));
    };
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(tally(r => r.user_name || 'Unknown')), 'By Person');

    const unhappy = rows.filter(r => r.helpful === false);
    if (unhappy.length)
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(unhappy.map(line)), 'Not helpful');

    // Record what was filtered, so a saved file can be trusted later.
    const applied = Object.entries({
      Search: f.search,
      Rating: f.rated === 'up' ? 'Helpful' : f.rated === 'down' ? 'Not helpful' : f.rated === 'none' ? 'Unrated' : '',
      From: f.from, To: f.to,
    }).filter(([, v]) => v).map(([k,v]) => ({ Filter:k, Value:v }));
    applied.push({ Filter: 'Rows exported', Value: String(rows.length) });
    applied.push({ Filter: 'Generated', Value: new Date().toLocaleString('en-IN') });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(applied), 'Filters applied');

    XLSX.writeFile(wb, `ai-questions-${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  if (!isAdmin) return <div style={{ padding:40, color:'var(--text2)', textAlign:'center' }}>Admin access required</div>;
  const set = (k,v) => setF(p => ({ ...p, [k]:v }));

  return (
    <div>
      <div style={{ marginBottom:14 }}>
        <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>AI Questions &amp; Answers</h2>
        <p style={{ fontSize:13, color:'var(--text2)' }}>Review what staff ask the assistant and how it answers, so you can fix documents or guidance.</p>
      </div>

      <div className="card" style={{ marginBottom:14, padding:12 }}>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr auto auto', gap:10, alignItems:'end' }}>
          <div><label style={{ fontSize:11, color:'var(--text2)' }}>Search question / answer</label>
            <input value={f.search} onChange={e => set('search', e.target.value)} placeholder="e.g. projector, wifi…" /></div>
          <div><label style={{ fontSize:11, color:'var(--text2)' }}>Rating</label>
            <select value={f.rated} onChange={e => set('rated', e.target.value)}>
              <option value="">All</option><option value="down">👎 Not helpful</option><option value="up">👍 Helpful</option><option value="none">Unrated</option>
            </select></div>
          <div><label style={{ fontSize:11, color:'var(--text2)' }}>From</label><input type="date" value={f.from} onChange={e => set('from', e.target.value)} /></div>
          <div><label style={{ fontSize:11, color:'var(--text2)' }}>To</label><input type="date" value={f.to} onChange={e => set('to', e.target.value)} /></div>
          <button className="btn btn-secondary" onClick={load}>Refresh</button>
          <button className="btn btn-secondary" onClick={exportXlsx} disabled={exporting}>
            {exporting ? 'Exporting…' : '⬇ Export'}
          </button>
        </div>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>
        : !rows.length ? <div className="empty-state"><p>No AI questions yet.</p></div>
        : (
          <div className="table-wrap"><table>
            <thead><tr><th>When</th><th>Who</th><th>Question</th><th>Docs</th><th>Rating</th><th>Speed</th><th></th></tr></thead>
            <tbody>{rows.map(r => (
              <React.Fragment key={r.id}>
                <tr style={{ cursor:'pointer' }} onClick={() => setOpen(open===r.id?null:r.id)}>
                  <td style={{ fontSize:12, color:'var(--text2)', whiteSpace:'nowrap' }}>{new Date(r.created_at).toLocaleString()}</td>
                  <td style={{ fontSize:12 }}>{r.user_name || '—'}</td>
                  <td style={{ fontSize:13 }}>{r.question}</td>
                  <td>{r.used_docs ? <span className="badge badge-completed">from docs</span> : <span className="badge badge-normal">general</span>}</td>
                  <td style={{ fontSize:14 }}>{r.helpful === true ? '👍' : r.helpful === false ? '👎' : <span style={{ color:'var(--text3)' }}>—</span>}</td>
                  <td style={{ fontSize:12, color:'var(--text2)' }}>{r.ms ? (r.ms/1000).toFixed(1)+'s' : '—'}</td>
                  <td style={{ fontSize:12, color:'var(--accent)' }}>{open===r.id?'Hide':'View answer'}</td>
                </tr>
                {open===r.id && (
                  <tr><td colSpan={7} style={{ background:'var(--bg3)' }}>
                    <div style={{ padding:'12px 14px' }}>
                      <div style={{ fontSize:12, fontWeight:600, color:'var(--text2)', marginBottom:4 }}>Answer <span style={{ fontWeight:400 }}>· {r.model}</span></div>
                      <div style={{ fontSize:13, whiteSpace:'pre-wrap', lineHeight:1.5 }}>{r.answer}</div>
                      {Array.isArray(r.sources) && r.sources.length>0 && (
                        <div style={{ marginTop:8, fontSize:12, color:'var(--text2)' }}>Sources: {r.sources.map((s,i)=>(
                          <span key={s.id}>{i>0?', ':''}<a href={`/api/documents/${s.id}/file`} target="_blank" rel="noopener noreferrer" style={{ color:'var(--accent)' }}>{s.title}</a></span>
                        ))}</div>
                      )}
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
